/**
 * ProductBadges — renders up to 2 product badges overlaid on the tile image.
 *
 * Supports:
 * - Per-badge custom backgroundColor and textColor (from SFRA base.js decorator)
 * - Date-gated display (badgeDisplayFrom/To scheduling)
 * - Falls back to theme accent colors when custom colors not provided
 *
 * Source references:
 * - sfcc-website/org_hec/cartridge/templates/default/product/components/productTileImage.isml
 * - sfcc-website/org_hec/cartridge/models/product/decorators/base.js (isScheduled logic)
 * - hec-composable/hec/components/product/productBadges.tsx
 */
import type { HecBadge } from "~/hec/types/product";

interface ProductBadgesProps {
  badges: HecBadge[];
  productId: string;
  /**
   * Background color for an LMN "LMN Required" badge that carries no per-badge
   * color of its own — Builder `lmnBadgeColor` (SFCC-11677). Mirrors composable
   * `productBadges.tsx`. When applied, text renders white.
   */
  lmnBadgeColor?: string;
}

export function ProductBadges({
  badges,
  productId,
  lmnBadgeColor,
}: ProductBadgesProps) {
  if (badges.length === 0) return null;

  return (
    <div className="absolute left-2 top-3 z-10 flex w-[60%] max-w-[60%] flex-col items-start gap-1">
      {badges.map((badge, index) => {
        const backgroundColor = badge.backgroundColor || lmnBadgeColor;
        const color =
          badge.textColor || (lmnBadgeColor ? "#ffffff" : undefined);
        const hasCustomColors = Boolean(backgroundColor || color);

        return (
          <span
            key={`${productId}-badge-${index}`}
            data-testid={`product-tile-badge-${productId}`}
            className={
              hasCustomColors
                ? "cursor-pointer rounded-[3px] px-[.3125rem] py-[.125rem] font-grotesque text-[13px] font-medium lg:py-[.025rem]"
                : "cursor-pointer bg-[hsl(var(--tile-badge-bg))] px-[.3125rem] py-[.125rem] font-grotesque text-[13px] font-medium text-primary-foreground lg:py-[.025rem]"
            }
            style={hasCustomColors ? { backgroundColor, color } : undefined}
          >
            {badge.text}
          </span>
        );
      })}
    </div>
  );
}
